import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Space,
  Divider,
  Alert,
  Typography,
  Table,
  Select,
  Tag,
  Card,
  Row,
  Col,
  Tooltip,
  DatePicker,
} from 'antd';
import {
  FileTextOutlined,
  DollarOutlined,
  CommentOutlined,
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PercentageOutlined,
  LinkOutlined,
  LockOutlined,
  UnlockOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { receiveBillRequest } from '../../redux/actions/billReceiptActions';
import { fetchOrderByIdRequest } from '../../containers/PurchaseOrder/actions';
import { deliveryAPI } from '../../api/config';

const { Text, Title } = Typography;
const { Option } = Select;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMoney = (v) =>
  v != null ? `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

const TAX_SLABS = ['0', '5', '12', '18', '28'];

/**
 * Compute per-item derived amounts from form values.
 */
const computeItemAmounts = (qty, rate, discountPct, taxSlab) => {
  const q = parseFloat(qty) || 0;
  const r = parseFloat(rate) || 0;
  const d = parseFloat(discountPct) || 0;
  const t = parseFloat(taxSlab) || 0;

  const subtotal = q * r;
  const discountAmt = (subtotal * d) / 100;
  const taxable = subtotal - discountAmt;
  const taxAmt = (taxable * t) / 100;
  const total = taxable + taxAmt;

  return {
    subtotal: subtotal.toFixed(2),
    discountAmt: discountAmt.toFixed(2),
    taxable: taxable.toFixed(2),
    taxAmt: taxAmt.toFixed(2),
    total: total.toFixed(2),
  };
};

const getNumericAmount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

const getAmountFromChallan = (challanData) => {
  const candidates = [
    challanData?.challan_amount,
    challanData?.final_amount,
    challanData?.total_amount,
    challanData?.amount,
  ];

  for (const candidate of candidates) {
    const amount = getNumericAmount(candidate);
    if (amount !== undefined) {
      return amount;
    }
  }

  return undefined;
};

const extractChallanPayload = (responseData) => {
  if (!responseData) return null;

  if (responseData.challan) return responseData.challan;
  if (responseData.data?.challan) return responseData.data.challan;
  if (responseData.data) return responseData.data;

  return responseData;
};

// ─── Item Row Summary ────────────────────────────────────────────────────────

const ItemSummaryRow = ({ item }) => {
  if (!item) return null;
  const { billed_quantity, billed_rate, discount, tax_slab } = item;
  const computed = computeItemAmounts(billed_quantity, billed_rate, discount, tax_slab);
  return (
    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
      Subtotal: <Text strong>₹{computed.subtotal}</Text>
      {parseFloat(computed.discountAmt) > 0 && (
        <> | Discount: <Text type="danger">−₹{computed.discountAmt}</Text></>
      )}
      {' '}| Taxable: <Text strong>₹{computed.taxable}</Text>
      {' '}| Tax ({tax_slab || 0}%): <Text>₹{computed.taxAmt}</Text>
      {' '}| <Text strong style={{ color: '#1890ff' }}>Total: ₹{computed.total}</Text>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * BillReceiptForm
 *
 * Props:
 *  - challan  : optional pre-selected pending-challan object
 *  - onSuccess: callback after successful submit
 *  - onCancel : callback for cancel button
 */
const BillReceiptForm = ({ challan, onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const { actionLoading, error } = useSelector((state) => state.billReceipts);
  const { currentOrder, loading: orderLoading } = useSelector((state) => state.orders);
  const [form] = Form.useForm();
  const [isAutofillingChallanAmounts, setIsAutofillingChallanAmounts] = useState(false);
  const [lockedChallanIndices, setLockedChallanIndices] = useState(new Set());
  const [unlockedChallanIndices, setUnlockedChallanIndices] = useState(new Set());
  const [unlockedLineItemIndices, setUnlockedLineItemIndices] = useState(new Set());
  // Track whether the user has submitted so we know when to act on the saga result.
  // 'idle' → not submitted; 'pending' → waiting for saga; closes form on success.
  const [submitPhase, setSubmitPhase] = useState('idle');

  // When actionLoading drops to false after a submission, the saga has settled.
  // Close the modal only on success; on error keep the form open so the user sees the message.
  useEffect(() => {
    if (submitPhase !== 'pending') return;
    if (actionLoading) return; // still in-flight
    // Saga has completed — check whether it succeeded or failed
    setSubmitPhase('idle');
    if (!error) {
      onSuccess?.();
    }
  }, [submitPhase, actionLoading, error, onSuccess]);

  const selectedChallans = useMemo(() => {
    if (Array.isArray(challan?.challans) && challan.challans.length > 0) {
      return challan.challans;
    }

    return challan ? [challan] : [];
  }, [challan]);

  const selectedPOId = selectedChallans[0]?.purchase_order_id;
  const selectedVendorName =
    selectedChallans[0]?.purchase_order?.vendor_name ||
    selectedChallans[0]?.vendor_name ||
    '—';
  const selectedPOAmount = selectedChallans[0]?.purchase_order?.total_amount;
  const prefilledChallanCount = selectedChallans.length;

  // ── Watched values / computed totals for smart prefill and summaries ─────
  const watchedItems = Form.useWatch('items', form) || [];
  const watchedChallanLinks = Form.useWatch('challan_links', form) || [];

  const challanPortionTotal = useMemo(
    () =>
      watchedChallanLinks.reduce((sum, link) => {
        return sum + (getNumericAmount(link?.challan_amount) || 0);
      }, 0),
    [watchedChallanLinks]
  );

  const lineItemTotals = useMemo(
    () =>
      watchedItems.reduce(
        (acc, item) => {
          const computed = computeItemAmounts(
            item?.billed_quantity,
            item?.billed_rate,
            item?.discount,
            item?.tax_slab
          );

          acc.taxable += parseFloat(computed.taxable) || 0;
          acc.taxAmt += parseFloat(computed.taxAmt) || 0;
          acc.total += parseFloat(computed.total) || 0;
          acc.discountAmt += parseFloat(computed.discountAmt) || 0;

          return acc;
        },
        { taxable: 0, taxAmt: 0, total: 0, discountAmt: 0 }
      ),
    [watchedItems]
  );

  // Pre-fill context from the challan that was clicked
  useEffect(() => {
    let isCancelled = false;

    const autofillChallanAmounts = async () => {
      if (!selectedPOId) {
        form.setFieldsValue({ challan_links: [{}] });
        setLockedChallanIndices(new Set());
        setUnlockedChallanIndices(new Set());
        setUnlockedLineItemIndices(new Set());
        return;
      }

      dispatch(fetchOrderByIdRequest(selectedPOId));

      const prefilledLinks = selectedChallans.map((c) => ({
        challan_number: c?.challan_number || '',
        challan_amount: getAmountFromChallan(c),
      }));

      const initiallyLockedIndices = new Set();
      prefilledLinks.forEach((link, idx) => {
        if (getNumericAmount(link?.challan_amount) !== undefined) {
          initiallyLockedIndices.add(idx);
        }
      });

      form.setFieldsValue({
        purchase_order_id: selectedPOId,
        challan_links: prefilledLinks.length > 0 ? prefilledLinks : [{}],
      });

      setLockedChallanIndices(initiallyLockedIndices);
      setUnlockedChallanIndices(new Set());
      setUnlockedLineItemIndices(new Set());

      const challansMissingAmount = selectedChallans.filter(
        (c) => c?.challan_number && getAmountFromChallan(c) === undefined
      );

      if (challansMissingAmount.length === 0) {
        return;
      }

      setIsAutofillingChallanAmounts(true);

      try {
        const lookups = await Promise.all(
          challansMissingAmount.map(async (pendingChallan) => {
            try {
              const response = await deliveryAPI.getChallanByNumber(
                pendingChallan.challan_number
              );
              const payload = extractChallanPayload(response?.data);
              const amount = getAmountFromChallan(payload);

              return {
                challan_number: pendingChallan.challan_number,
                challan_amount: amount,
              };
            } catch (error) {
              return {
                challan_number: pendingChallan.challan_number,
                challan_amount: undefined,
              };
            }
          })
        );

        if (isCancelled) {
          return;
        }

        const amountByChallanNumber = new Map(
          lookups
            .filter((entry) => entry.challan_amount !== undefined)
            .map((entry) => [entry.challan_number, entry.challan_amount])
        );

        if (amountByChallanNumber.size === 0) {
          return;
        }

        const currentLinks = form.getFieldValue('challan_links') || prefilledLinks;

        const enrichedLinks = currentLinks.map((link) => {
          const alreadySetAmount = getNumericAmount(link?.challan_amount);
          if (alreadySetAmount !== undefined) {
            return link;
          }

          const fetchedAmount = amountByChallanNumber.get(link?.challan_number);
          if (fetchedAmount === undefined) {
            return link;
          }

          return {
            ...link,
            challan_amount: fetchedAmount,
          };
        });

        form.setFieldsValue({ challan_links: enrichedLinks });

        // Mark newly auto-filled indices as locked
        setLockedChallanIndices((prev) => {
          const newLockedIndices = new Set(prev);
          currentLinks.forEach((link, idx) => {
            const alreadySetAmount = getNumericAmount(link?.challan_amount);
            if (alreadySetAmount === undefined) {
              const fetchedAmount = amountByChallanNumber.get(link?.challan_number);
              if (fetchedAmount !== undefined) {
                newLockedIndices.add(idx);
              }
            }
          });

          return newLockedIndices;
        });
      } finally {
        if (!isCancelled) {
          setIsAutofillingChallanAmounts(false);
        }
      }
    };

    autofillChallanAmounts();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, form, selectedChallans, selectedPOId]);

  // Auto-populate line items once PO is fetched
  useEffect(() => {
    if (currentOrder && currentOrder.items && currentOrder.items.length > 0 && currentOrder.id === selectedPOId) {
      const formItems = currentOrder.items.map(item => ({
        purchase_order_item_id: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        unit: item.unit,
        // Default billed quantity to what was ordered. 
        // For partial bills, user can adjust this down.
        billed_quantity: item.quantity,
        billed_rate: item.rate,
        tax_slab: item.tax_slab || '0',
        discount: item.discount || 0,
      }));
      form.setFieldsValue({ items: formItems });
    }
  }, [currentOrder, form, selectedPOId]);

  // Prefill vendor GST from the PO's vendor master record, editable in case
  // the number printed on this specific invoice differs.
  useEffect(() => {
    if (
      currentOrder?.vendor?.gst_number &&
      currentOrder.id === selectedPOId &&
      !form.isFieldTouched('vendor_gst_number') &&
      !form.getFieldValue('vendor_gst_number')
    ) {
      form.setFieldsValue({ vendor_gst_number: currentOrder.vendor.gst_number });
    }
  }, [currentOrder, form, selectedPOId]);

  // Smart prefill: populate key amount fields when user has not edited them yet.
  useEffect(() => {
    const updates = {};
    const suggestedBillAmount = challanPortionTotal > 0 ? challanPortionTotal : lineItemTotals.total;

    const currentBillAmount = getNumericAmount(form.getFieldValue('bill_amount'));
    if (
      !form.isFieldTouched('bill_amount') &&
      (currentBillAmount === undefined || currentBillAmount === 0) &&
      suggestedBillAmount > 0
    ) {
      updates.bill_amount = Number(suggestedBillAmount.toFixed(2));
    }

    const currentTaxableAmount = getNumericAmount(form.getFieldValue('taxable_amount'));
    if (
      !form.isFieldTouched('taxable_amount') &&
      (currentTaxableAmount === undefined || currentTaxableAmount === 0) &&
      lineItemTotals.taxable > 0
    ) {
      updates.taxable_amount = Number(lineItemTotals.taxable.toFixed(2));
    }

    const currentTaxAmount = getNumericAmount(form.getFieldValue('tax_amount'));
    if (
      !form.isFieldTouched('tax_amount') &&
      (currentTaxAmount === undefined || currentTaxAmount === 0) &&
      lineItemTotals.taxAmt > 0
    ) {
      updates.tax_amount = Number(lineItemTotals.taxAmt.toFixed(2));
    }

    const currentDiscountAmount = getNumericAmount(form.getFieldValue('discount_amount'));
    if (
      !form.isFieldTouched('discount_amount') &&
      (currentDiscountAmount === undefined || currentDiscountAmount === 0) &&
      lineItemTotals.discountAmt > 0
    ) {
      updates.discount_amount = Number(lineItemTotals.discountAmt.toFixed(2));
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [form, challanPortionTotal, lineItemTotals.total, lineItemTotals.taxable, lineItemTotals.taxAmt, lineItemTotals.discountAmt]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleFinish = (values) => {
    const items = (values.items || [])
      .filter((item) => item && item.purchase_order_item_id)
      .map((item) => ({
        purchase_order_item_id: item.purchase_order_item_id,
        product_id: item.product_id || undefined,
        product_name: item.product_name || undefined,
        unit: item.unit || undefined,
        billed_quantity: parseFloat(item.billed_quantity) || 0,
        billed_rate: parseFloat(item.billed_rate) || 0,
        tax_slab: item.tax_slab || '0',
        discount: parseFloat(item.discount) || 0,
      }));

    const payload = {
      purchase_order_id: selectedPOId ?? values.purchase_order_id,
      challan_links: (values.challan_links || []).map((cl) => ({
        challan_number: cl.challan_number,
        challan_amount: parseFloat(cl.challan_amount) || 0,
      })),
      bill_number: values.bill_number,
      bill_date: values.bill_date ? values.bill_date.format('YYYY-MM-DD') : undefined,
      vendor_gst_number: values.vendor_gst_number || undefined,
      bill_amount: parseFloat(values.bill_amount) || 0,
      taxable_amount: values.taxable_amount ? parseFloat(values.taxable_amount) : undefined,
      tax_amount: values.tax_amount ? parseFloat(values.tax_amount) : undefined,
      discount_amount: values.discount_amount ? parseFloat(values.discount_amount) : undefined,
      remarks: values.remarks || undefined,
      items: items.length > 0 ? items : undefined,
    };

    setSubmitPhase('pending');
    dispatch(receiveBillRequest(payload));
  };

  return (
    <div className="bill-form-wrapper">
      {/* Context info */}
      {challan && (
        <>
          <div className="bill-form-context-card">
            <Row gutter={[16, 8]}>
              <Col xs={12} sm={6}>
                <div className="bill-form-context-label">PO Number</div>
                <div className="bill-form-context-value">#{selectedPOId}</div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="bill-form-context-label">Vendor</div>
                <div className="bill-form-context-value">
                  {selectedVendorName}
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="bill-form-context-label">Challans</div>
                <Tooltip
                  title={selectedChallans
                    .map((c) => c?.challan_number)
                    .filter(Boolean)
                    .join(', ')}
                >
                  <div className="bill-form-context-value" style={{ color: '#1890ff' }}>
                    {prefilledChallanCount > 1
                      ? `${prefilledChallanCount} challans selected`
                      : selectedChallans[0]?.challan_number || '—'}
                  </div>
                </Tooltip>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                  {prefilledChallanCount > 1
                    ? selectedChallans
                        .map((c) => c?.challan_number)
                        .filter(Boolean)
                        .slice(0, 2)
                        .join(', ')
                    : 'Single challan'}
                  {prefilledChallanCount > 2 ? ' …' : ''}
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="bill-form-context-label">PO Amount</div>
                <div className="bill-form-context-value">
                  {fmtMoney(selectedPOAmount)}
                </div>
              </Col>
            </Row>
          </div>
          <Divider style={{ margin: '16px 0' }} />
        </>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark="optional"
        autoComplete="off"
        disabled={orderLoading} // Prevent interaction while fetching PO details
      >
        {orderLoading && (
          <Alert
            type="info"
            message="Fetching Purchase Order details..."
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {isAutofillingChallanAmounts && (
          <Alert
            type="info"
            message="Auto-filling challan portion amount from delivery challan final amount..."
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* ── Challan Links ─────────────────────────────────────────────────── */}
        <div className="bill-form-section-title">
          <LinkOutlined style={{ marginRight: 6 }} />
          Delivery Challan Links
          <Tooltip title="One bill can cover multiple delivery challans">
            <InfoCircleOutlined style={{ marginLeft: 8, color: '#aaa', fontSize: 13 }} />
          </Tooltip>
        </div>

        <Form.List name="challan_links">
          {(fields, { add, remove }) => (
            <div>
              {fields.map(({ key, name, ...rest }) => {
                const isChallanLocked = lockedChallanIndices.has(name) && !unlockedChallanIndices.has(name);
                const isPrefilledChallan = prefilledChallanCount > 0 && name < prefilledChallanCount;

                return (
                <Card
                  key={key}
                  size="small"
                  style={{ marginBottom: 10, background: '#fafcff', borderColor: isChallanLocked ? '#1890ff' : '#d6e4ff' }}
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <Row gutter={12} align="middle">
                    <Col xs={24} sm={11}>
                      <Form.Item
                        {...rest}
                        name={[name, 'challan_number']}
                        label="Challan Number"
                        rules={[{ required: true, message: 'Challan number is required' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="e.g. DC-001"
                          prefix={<FileTextOutlined style={{ color: '#bbb' }} />}
                          // Lock pre-selected challan rows from pending list context.
                          disabled={isPrefilledChallan}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={11}>
                      <Form.Item
                        {...rest}
                        name={[name, 'challan_amount']}
                        label={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>Challan Portion Amount (₹)</span>
                            {isChallanLocked && (
                              <Tooltip title="Auto-filled. Click unlock to edit.">
                                <LockOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                              </Tooltip>
                            )}
                          </div>
                        }
                        rules={[
                          { required: true, message: 'Challan amount is required' },
                          { type: 'number', min: 0, message: 'Must be ≥ 0' },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          precision={2}
                          placeholder="0.00"
                          prefix="₹"
                          disabled={isChallanLocked}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={2} style={{ textAlign: 'center' }}>
                      {isChallanLocked && (
                        <Tooltip title="Unlock to edit this amount">
                          <Button
                            type="text"
                            size="small"
                            icon={<UnlockOutlined style={{ color: '#1890ff' }} />}
                            onClick={() => {
                              setUnlockedChallanIndices((prev) => new Set([...prev, name]));
                            }}
                            style={{ marginTop: 22 }}
                          />
                        </Tooltip>
                      )}
                      {fields.length > 1 && !isChallanLocked && (
                        <Tooltip title="Remove challan">
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            style={{ marginTop: 22 }}
                          />
                        </Tooltip>
                      )}
                    </Col>
                  </Row>
                </Card>
              );
              })}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #d9f7be',
                  background: '#f6ffed',
                  marginBottom: 12,
                }}
              >
                <Text strong>Total Challan Portion Amount</Text>
                <Text strong style={{ color: '#389e0d' }}>
                  {fmtMoney(challanPortionTotal)}
                </Text>
              </div>
              <Button
                type="dashed"
                onClick={() => add()}
                icon={<PlusOutlined />}
                style={{ width: '100%', marginBottom: 16 }}
              >
                Add Another Challan
              </Button>
            </div>
          )}
        </Form.List>

        <Divider style={{ margin: '4px 0 20px' }} />

        {/* ── Bill Header ───────────────────────────────────────────────────── */}
        <div className="bill-form-section-title">
          <FileTextOutlined style={{ marginRight: 6 }} />
          Vendor Bill Details
        </div>

        <Row gutter={16}>
          <Col xs={24} sm={10}>
            <Form.Item
              name="bill_number"
              label="Bill / Invoice Number"
              rules={[
                { required: true, message: 'Please enter the vendor invoice number' },
                { max: 100, message: 'Max 100 characters' },
              ]}
            >
              <Input
                prefix={<FileTextOutlined style={{ color: '#bbb' }} />}
                placeholder="e.g. INV-2025-0099"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={7}>
            <Form.Item name="bill_date" label="Bill Date">
              <DatePicker
                style={{ width: '100%' }}
                size="large"
                format="DD/MM/YYYY"
                suffixIcon={<CalendarOutlined style={{ color: '#bbb' }} />}
                placeholder="As printed on invoice"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={7}>
            <Form.Item
              name="vendor_gst_number"
              label="Vendor GST Number"
              rules={[{ max: 20, message: 'Max 20 characters' }]}
              normalize={(value) => (value || '').toUpperCase()}
            >
              <Input placeholder="As printed on invoice" size="large" />
            </Form.Item>
          </Col>
        </Row>

        {/* ── Bill Amounts ──────────────────────────────────────────────────── */}
        <div className="bill-form-section-title">
          <DollarOutlined style={{ marginRight: 6 }} />
          Bill Amounts
        </div>

        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="bill_amount"
              label="Grand Total (₹)"
              rules={[
                { required: true, message: 'Bill total is required' },
                { type: 'number', min: 0, message: 'Must be ≥ 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                min={0}
                precision={2}
                placeholder="0.00"
                prefix="₹"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="taxable_amount" label="Taxable Amount (₹)">
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                min={0}
                precision={2}
                placeholder="0.00"
                prefix="₹"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="tax_amount" label="Tax / GST Amount (₹)">
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                min={0}
                precision={2}
                placeholder="0.00"
                prefix="₹"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="discount_amount" label="Discount Amount (₹)">
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                min={0}
                precision={2}
                placeholder="0.00"
                prefix="₹"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ── Line Items ────────────────────────────────────────────────────── */}
        <Divider style={{ margin: '8px 0 16px' }} />
        <div className="bill-form-section-title">
          <FileTextOutlined style={{ marginRight: 6 }} />
          Line Items
          <Tooltip title="Optional but recommended. Enables automatic 3-way reconciliation against the PO.">
            <InfoCircleOutlined style={{ marginLeft: 8, color: '#aaa', fontSize: 13 }} />
          </Tooltip>
        </div>

        <Form.List name="items">
          {(fields, { add, remove }) => (
            <div>
              {fields.map(({ key, name, ...rest }, idx) => {
                const watchedItem = watchedItems[name] || {};
                const isLineItemLocked = !unlockedLineItemIndices.has(name);
                return (
                  <Card
                    key={key}
                    size="small"
                    title={
                      <Space>
                        <Tag color="blue">Item {idx + 1}</Tag>
                        {watchedItem.product_name && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {watchedItem.product_name}
                          </Text>
                        )}
                        {isLineItemLocked ? (
                          <Tooltip title="Locked. Click unlock to edit fields.">
                            <LockOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Editable">
                            <UnlockOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                          </Tooltip>
                        )}
                      </Space>
                    }
                    extra={
                      <Space size={0}>
                        {isLineItemLocked ? (
                          <Tooltip title="Unlock this line item">
                            <Button
                              type="text"
                              size="small"
                              icon={<UnlockOutlined style={{ color: '#1890ff' }} />}
                              onClick={() => {
                                setUnlockedLineItemIndices((prev) => new Set([...prev, name]));
                              }}
                            >
                              Unlock
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Lock this line item">
                            <Button
                              type="text"
                              size="small"
                              icon={<LockOutlined style={{ color: '#8c8c8c' }} />}
                              onClick={() => {
                                setUnlockedLineItemIndices((prev) => {
                                  const next = new Set(prev);
                                  next.delete(name);
                                  return next;
                                });
                              }}
                            >
                              Lock
                            </Button>
                          </Tooltip>
                        )}
                        <Button
                          danger
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          disabled={isLineItemLocked}
                        >
                          Remove
                        </Button>
                      </Space>
                    }
                    style={{ marginBottom: 12, borderColor: isLineItemLocked ? '#d6e4ff' : '#f0f0f0' }}
                    bodyStyle={{ paddingTop: 12 }}
                  >
                    <Row gutter={12}>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          {...rest}
                          name={[name, 'purchase_order_item_id']}
                          label="PO Item ID"
                          rules={[{ required: true, message: 'Required' }]}
                          style={{ marginBottom: 10 }}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={1}
                            placeholder="PO Item ID"
                            disabled={isLineItemLocked}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          {...rest}
                          name={[name, 'product_name']}
                          label="Product Name"
                          style={{ marginBottom: 10 }}
                        >
                          <Input placeholder="e.g. A4 Paper Ream" disabled={isLineItemLocked} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          {...rest}
                          name={[name, 'unit']}
                          label="Unit"
                          style={{ marginBottom: 10 }}
                        >
                          <Input placeholder="box / ltr / kg" disabled={isLineItemLocked} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col xs={12} sm={6}>
                        <Form.Item
                          {...rest}
                          name={[name, 'billed_quantity']}
                          label="Qty"
                          rules={[
                            { required: true, message: 'Required' },
                            { type: 'number', min: 0.0001, message: 'Must be > 0' },
                          ]}
                          style={{ marginBottom: 10 }}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={4}
                            placeholder="0"
                            disabled={isLineItemLocked}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Form.Item
                          {...rest}
                          name={[name, 'billed_rate']}
                          label="Rate (₹)"
                          rules={[
                            { required: true, message: 'Required' },
                            { type: 'number', min: 0, message: 'Must be ≥ 0' },
                          ]}
                          style={{ marginBottom: 10 }}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={4}
                            placeholder="0.00"
                            prefix="₹"
                            disabled={isLineItemLocked}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Form.Item
                          {...rest}
                          name={[name, 'tax_slab']}
                          label="GST Slab (%)"
                          style={{ marginBottom: 10 }}
                          initialValue="18"
                        >
                          <Select placeholder="Select GST %" disabled={isLineItemLocked}>
                            {TAX_SLABS.map((t) => (
                              <Option key={t} value={t}>
                                {t}%
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Form.Item
                          {...rest}
                          name={[name, 'discount']}
                          label={
                            <span>
                              <PercentageOutlined style={{ marginRight: 4, color: '#bbb' }} />
                              Discount (%)
                            </span>
                          }
                          style={{ marginBottom: 10 }}
                          initialValue={0}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={100}
                            precision={4}
                            placeholder="0"
                            disabled={isLineItemLocked}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <ItemSummaryRow item={watchedItem} />
                  </Card>
                );
              })}

              <Button
                type="dashed"
                onClick={() => add({ tax_slab: '18', discount: 0 })}
                icon={<PlusOutlined />}
                style={{ width: '100%', marginBottom: 12 }}
              >
                Add Line Item
              </Button>

              {fields.length === 0 && (
                <Alert
                  type="info"
                  showIcon
                  message="No line items added"
                  description="Adding line items enables automatic 3-way reconciliation (Bill vs PO). You can skip this for now."
                  style={{ marginBottom: 16 }}
                />
              )}
            </div>
          )}
        </Form.List>

        {/* ── Remarks ───────────────────────────────────────────────────────── */}
        <Form.Item
          name="remarks"
          label={
            <span>
              <CommentOutlined style={{ marginRight: 6, color: '#bbb' }} />
              Remarks
            </span>
          }
        >
          <Input.TextArea
            rows={3}
            placeholder="Optional notes about this bill…"
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* ── Actions ────────────────────────────────────────────────────────── */}
        <Divider style={{ margin: '8px 0 16px' }} />
        <Form.Item style={{ marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            {onCancel && (
              <Button size="large" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={actionLoading || orderLoading}
              icon={<FileTextOutlined />}
            >
              Record Bill Receipt
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default BillReceiptForm;
